import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createParagraphNode, $createTextNode, $getRoot, ParagraphNode, RootNode } from 'lexical';
import { MutableRefObject, useEffect } from 'react';
import { ParameterDto } from '@/Domain/api/Action/dto/Action.dto';
import { parseValueToEditor } from '@/Domain/components/Action/Lexical/helper/parseValueToEditor';
import { BadgeNode } from '@/Domain/components/Action/Lexical/node/BadgeNode';

type Props = {
  value: string;
  parameters?: ParameterDto[];
  onLineCountChange?: (count: number) => void;
  suppressExternalValueRef?: MutableRefObject<boolean>;
};

function buildEditorFromString(value: string, parameters?: ParameterDto[]): RootNode {
  const root = $getRoot();
  root.clear();

  const lines = value.split('\n');

  const makeLineNodes = (line: string) => {
    const parts = parseValueToEditor(line, parameters);
    const paragraph = $createParagraphNode();
    for (const part of parts) {
      if (typeof part === 'string') {
        paragraph.append($createTextNode(part));
      } else {
        paragraph.append(BadgeNode.importJSON(part));
      }
    }
    return paragraph;
  };

  if (lines.length === 0) {
    root.append($createParagraphNode());
  } else {
    for (const line of lines) {
      root.append(makeLineNodes(line));
    }
  }

  return root;
}

function serializeEditorToString(): string {
  const root = $getRoot();
  const lines: string[] = [];

  for (const block of root.getChildren()) {
    if (block.getType() !== 'paragraph') {
      lines.push(block.getTextContent());
      continue;
    }
    const parts: string[] = [];
    for (const node of (block as ParagraphNode).getChildren()) {
      if (node.getType() === 'badge') {
        const badge = node as unknown as BadgeNode;
        parts.push(badge.getLabel());
      } else {
        parts.push(node.getTextContent());
      }
    }
    lines.push(parts.join(''));
  }

  return lines.join('\n').replace(/\u00a0/g, ' ');
}

export const InitFromFormPlugin = ({
  value,
  parameters,
  onLineCountChange,
  suppressExternalValueRef,
}: Props) => {
  const [editor] = useLexicalComposerContext();

  useEffect(
    () =>
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          const root = $getRoot();
          const count = Math.max(1, root.getChildrenSize());
          onLineCountChange?.(count);
        });
      }),
    [editor, onLineCountChange],
  );

  useEffect(() => {
    const incoming = (value ?? '').replace(/\u00a0/g, ' ');
    let shouldImport = false;

    editor.getEditorState().read(() => {
      if (suppressExternalValueRef?.current) return;
      const current = serializeEditorToString();
      if (current !== incoming) shouldImport = true;
    });

    if (shouldImport) {
      editor.update(() => {
        buildEditorFromString(incoming, parameters);
      });
    }
  }, [editor, value, parameters, suppressExternalValueRef]);

  return null;
};
